import React, { useState } from "react";

import { useSelector } from "react-redux";

import {
  faChevronDown,
  faChevronUp,
  faCircleExclamation,
  faPaperPlane,
  faCheckCircle,
  faFilePen,
  faPencil,
  faEye,
} from "@fortawesome/pro-solid-svg-icons";
import { faFilePdf } from "@fortawesome/pro-light-svg-icons";
import { Collapse, Divider, Grid } from "@material-ui/core";
import { MultiButton } from "components/Button";
import { MultiButtonOption } from "components/Button/MultiButton/types";
import IconTextButton from "components/Button/IconTextButton";
import Badge from "components/Badge";
import { PaddedPaper } from "components/Paper";
import styled from "styled-components/macro";
import { BoldText, Text } from "components/Text";
import StatusText from "components/Text/StatusText";

import { EContractParty, EContractTypeLabel } from "constants/eContract";
import { ModalTypes } from "constants/navigation";
import {
  EContractAgreementPermission,
  EContractPermission,
} from "constants/permissions";

import {
  getEContractStatus,
  getEContractStatusColor,
  getEContractCodeVersionAndStatusText,
  hasBeenSentToBuyer,
  hasBeenSentToVendor,
} from "lib/econtracts";
import { openModalLink } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import {
  getBuyerByEContractAgreementId,
  getEContractAgreementById,
  getSaleLotIdsByAgreementId,
  getVendorByEContractAgreementId,
  selectActiveEContractByEContractAgreementId,
  selectEContractSpecialConditionsByAgreementId,
  selectExtraLotCountByActiveEContractCode,
} from "selectors";
import { getBusinesses, getEContracts } from "selectors/root";

import { useIsMobile, useToggle } from "hooks";

import { EContractSendEmailModal } from "./sendEmailModal";
import { EContractConditionsModal } from "./eContractConditionsModal";
import { EContractSpecialConditionsEditor } from "./eContractSpecialConditionsEditor";
import { Row } from "components/Layout";
import { SmallFaIcon } from "components/FaIcon";
import { formatUTCToLocalDateString } from "lib/timeFormats";
import { colors } from "constants/theme";
import { pluralize } from "lib/pluralize";

interface EContractActionsProps {
  agreement: EContractAgreement;
  eContract?: EContract;
  onDraftNew: () => void;
  onReview: () => void;
}

const EContractActions = ({
  agreement,
  eContract,
  onDraftNew,
  onReview,
}: EContractActionsProps): React.JSX.Element => {
  const [emailDialogParty, setEmailDialogParty] =
    useState<EContractParty | null>(null);

  const canGenerateContract = hasPermission(
    agreement,
    EContractAgreementPermission.generateContract,
  );
  const canRead = eContract
    ? hasPermission(eContract, EContractPermission.read)
    : false;
  const canAccept = eContract
    ? hasPermission(eContract, EContractPermission.accept)
    : false;
  const isVoided = !!eContract?.voidedOn;
  const isNotDrafted = !eContract;

  const matchingLotIds = useSelector(getSaleLotIdsByAgreementId(agreement.id));
  const hasMatchingLots = matchingLotIds.length > 0;

  const buttons: MultiButtonOption[] = [
    hasMatchingLots &&
      (isNotDrafted || isVoided) && {
        title: "Review",
        default: true,
        icon: faPencil,
        isDisabled: !canGenerateContract,
        onClick: onDraftNew,
      },
    !!eContract && {
      title: "PDF",
      icon: faFilePdf,
      isDisabled: !canRead,
      onClick: () => openModalLink(ModalTypes.EContract, { id: eContract.id }),
      default: !isVoided,
    },
    !!eContract &&
      !isVoided && {
        title: "Review",
        icon: faEye,
        onClick: onReview,
      },
    !!eContract &&
      !isVoided && {
        title: "Send To Vendor",
        icon: faPaperPlane,
        isDisabled: !canAccept,
        onClick: () => setEmailDialogParty(EContractParty.VENDOR),
      },
    !!eContract &&
      !isVoided && {
        title: "Send To Buyer",
        icon: faPaperPlane,
        isDisabled: !canAccept,
        onClick: () => setEmailDialogParty(EContractParty.BUYER),
      },
  ].filter(Boolean);

  const eContractStatus = getEContractStatus(eContract);

  return (
    <>
      <Grid container xs={12}>
        <Grid xs={6} container>
          {eContractStatus && (
            <Badge
              color={getEContractStatusColor(eContractStatus)}
              bold
              uppercase
            >
              {eContractStatus}
            </Badge>
          )}
        </Grid>
        <Grid xs={6} container justifyContent="flex-end">
          <MultiButton buttons={buttons} />
        </Grid>
      </Grid>

      {emailDialogParty && eContract && (
        <EContractSendEmailModal
          eContractId={eContract.id}
          onClose={() => setEmailDialogParty(null)}
          party={emailDialogParty}
        />
      )}
    </>
  );
};

interface EContractAgreementSummaryCardProps {
  agreementId: string;
  generateContractHandler: () => void;
  eContractAgreementHandlesSpecialConditions: boolean;
}

const PreviousVersion = ({
  eContract,
}: {
  eContract: EContract;
}): React.JSX.Element => {
  const hasDocument = !!(
    eContract.documentId ||
    eContract.buyerDocumentId ||
    eContract.vendorDocumentId
  );
  return (
    <Row justifyBetween alignCenter padding={1} gridGap={2}>
      <StatusText fontSize="beta">
        {getEContractCodeVersionAndStatusText(eContract)}
      </StatusText>
      <IconTextButton
        disabled={
          !hasDocument || !hasPermission(eContract, EContractPermission.read)
        }
        icon={faFilePdf}
        inline
        onClick={() =>
          openModalLink(ModalTypes.EContract, {
            id: eContract.id,
            readOnly: true,
          })
        }
      >
        <BoldText>Open PDF</BoldText>
      </IconTextButton>
    </Row>
  );
};

const EContractPaper = styled(PaddedPaper)<{
  borderColor: keyof typeof colors;
}>`
  border-left: ${({ theme }) => theme.space[1]}px solid
    ${({ theme, borderColor }) => theme.colors[borderColor]};
`;

const getDocumentStatusIcon = (
  acceptedOn?: string | null,
  hasBeenSent?: boolean,
) => {
  if (acceptedOn) {
    return <SmallFaIcon color="success" icon={faCheckCircle} />;
  }
  if (hasBeenSent) {
    return <SmallFaIcon color="inProgress" icon={faPaperPlane} />;
  }
  return <SmallFaIcon color="gray78" icon={faFilePen} />;
};

export const EContractAgreementSummaryCard = ({
  agreementId,
  generateContractHandler,
  eContractAgreementHandlesSpecialConditions,
}: EContractAgreementSummaryCardProps): React.JSX.Element | null => {
  const agreement = useSelector(getEContractAgreementById(agreementId));

  const specialConditionsByAgreementId = useSelector(
    selectEContractSpecialConditionsByAgreementId,
  );

  const allEContracts = useSelector(getEContracts);

  const extraLotCountByCode = useSelector(
    selectExtraLotCountByActiveEContractCode,
  );

  const isMobile = useIsMobile();

  const businesses = useSelector(getBusinesses);

  const eContracts = (agreement?.econtractIds ?? [])
    .map(id => (allEContracts as Record<string, EContract>)[id])
    .filter(Boolean)
    .sort((a, b) => b.version - a.version);

  const [latestEContract, ...previousEContracts] = eContracts;

  const buyerFromEContract = useSelector(
    getBuyerByEContractAgreementId(agreementId),
  );
  const vendorFromEContract = useSelector(
    getVendorByEContractAgreementId(agreementId),
  );

  const activeEContractByAgreementIdLookup = useSelector(
    selectActiveEContractByEContractAgreementId,
  );

  const [showPreviousVersions, toggleShowPreviousVersions] = useToggle(false);
  const [showConditionsModal, setShowConditionsModal] = useState(false);

  if (!agreement) {
    return null;
  }

  const activeEContract =
    activeEContractByAgreementIdLookup[agreementId] || null;

  const specialConditions = specialConditionsByAgreementId[agreement.id] ?? [];

  const extraLotCount = extraLotCountByCode[latestEContract?.code] || 0;

  const buyer =
    buyerFromEContract ??
    (businesses as Record<string, Business>)[agreement.buyerBusinessId] ??
    null;
  const vendor =
    vendorFromEContract ??
    (businesses as Record<string, Business>)[agreement.vendorBusinessId] ??
    null;

  const typeLabel = latestEContract
    ? EContractTypeLabel[latestEContract.type] || latestEContract.type
    : null;

  const borderColor = latestEContract
    ? getEContractStatusColor(
        getEContractStatus(activeEContract ?? latestEContract),
      )
    : "gray78";

  const buyerSent = hasBeenSentToBuyer(activeEContract);
  const vendorSent = hasBeenSentToVendor(activeEContract);

  const acceptedByBuyer = !!activeEContract?.buyerAcceptedOn;
  const acceptedByVendor = !!activeEContract?.vendorAcceptedOn;

  const openConditionsModal = () => setShowConditionsModal(true);

  const extraLotWarningText =
    !latestEContract?.voidedOn && extraLotCount > 0
      ? `${extraLotCount} ${pluralize("lot", extraLotCount)} ${extraLotCount === 1 ? "was" : "were"} created after this contract was generated. To update the contract, void it and click Draft New.`
      : null;

  return (
    <EContractPaper elevation={2} gap={2} fullHeight borderColor={borderColor}>
      <Grid
        container
        xs={12}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item xs={4} sm={8}>
          {latestEContract ? (
            <Text fontSize="Charlie">
              {latestEContract.code} v{latestEContract.version}
            </Text>
          ) : (
            <Text fontSize="Charlie" italic>
              No contract generated
            </Text>
          )}
        </Grid>
        {typeLabel && (
          <Grid container xs={8} sm={4} justifyContent="flex-end">
            <Text italic>{typeLabel}</Text>
          </Grid>
        )}
        {latestEContract?.voidedOn && (
          <Grid xs={12}>
            <BoldText color="errorRed">
              Voided On: {formatUTCToLocalDateString(latestEContract.voidedOn)}
            </BoldText>
          </Grid>
        )}
        {extraLotWarningText && (
          <Row alignCenter gridGap={1}>
            <SmallFaIcon color="warning" icon={faCircleExclamation} />
            <Text>{extraLotWarningText}</Text>
          </Row>
        )}
        <Grid />
      </Grid>

      <EContractActions
        agreement={agreement}
        eContract={latestEContract}
        onDraftNew={
          eContractAgreementHandlesSpecialConditions
            ? openConditionsModal
            : generateContractHandler
        }
        onReview={() => setShowConditionsModal(true)}
      />

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          md={6}
          container
          style={isMobile ? { marginBottom: "12px" } : {}}
        >
          <Grid item xs={3} sm={2} md={12}>
            <Text italic>
              {getDocumentStatusIcon(
                activeEContract?.vendorAcceptedOn,
                vendorSent,
              )}{" "}
              Vendor
            </Text>
          </Grid>
          <Grid item xs={9} sm={10} md={12}>
            <BoldText fontSize="beta">{vendor?.name ?? "-"}</BoldText>
            {acceptedByVendor && (
              <Row alignCenter gridGap={1}>
                Accepted on:{" "}
                {formatUTCToLocalDateString(activeEContract.vendorAcceptedOn)}
              </Row>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={6} container>
          <Grid item xs={3} sm={2} md={12}>
            <Text italic>
              {getDocumentStatusIcon(
                activeEContract?.buyerAcceptedOn,
                buyerSent,
              )}{" "}
              Buyer
            </Text>
          </Grid>
          <Grid item xs={9} sm={10} md={12}>
            <BoldText fontSize="beta">{buyer?.name ?? "-"}</BoldText>
            {acceptedByBuyer && (
              <Row alignCenter gridGap={1}>
                Accepted on:{" "}
                {formatUTCToLocalDateString(activeEContract.buyerAcceptedOn)}
              </Row>
            )}
          </Grid>
        </Grid>
      </Grid>

      {eContractAgreementHandlesSpecialConditions && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <BoldText block>Special Conditions</BoldText>
            <EContractSpecialConditionsEditor
              agreementId={agreement.id}
              conditions={specialConditions}
              readOnly
            />
          </Grid>
        </>
      )}

      {previousEContracts.length > 0 && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid container justifyContent="flex-end">
            <IconTextButton
              icon={showPreviousVersions ? faChevronUp : faChevronDown}
              iconPosition="right"
              inline
              onClick={toggleShowPreviousVersions}
            >
              <BoldText>
                {showPreviousVersions ? "Hide" : "Show"} Previous Versions
              </BoldText>
            </IconTextButton>
          </Grid>
          <Collapse in={showPreviousVersions}>
            {previousEContracts.map(eContract => (
              <PreviousVersion eContract={eContract} key={eContract.id} />
            ))}
          </Collapse>
        </>
      )}

      {showConditionsModal && (
        <EContractConditionsModal
          agreement={agreement}
          conditions={specialConditions}
          onClose={() => setShowConditionsModal(false)}
          onGenerateContract={generateContractHandler}
          readOnly={!!activeEContract}
        />
      )}
    </EContractPaper>
  );
};
